import { useState } from 'react';
import { LogIn } from 'lucide-react';
import { getApiBase } from './apiBase';

const WEBSITE_SIGNUP_URL =
  process.env.REACT_APP_WEBSITE_SIGNUP_URL || 'https://www.slpmicrosystems.com/signup';

export default function ToolLogin({ onSuccess, onBack }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = login.trim();
    if (!trimmed || !password) {
      setError('Email or username and password required');
      return;
    }
    setLoading(true);
    try {
      const API_BASE = getApiBase();
      const url = `${API_BASE}/auth/tool/login`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ login: trimmed, username: trimmed, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || data.message || `Request failed (${res.status}). Requested: ${url}`);
        return;
      }
      const meRes = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (!meRes.ok || !(await meRes.json().catch(() => ({})))?.toolUser?.id) {
        setError(
          'Signed in, but your session did not save. Check IDENTITY_MONGODB_URI on Heroku, COOKIE_DOMAIN, and SESSION_COOKIE_SAMESITE if API and app are on different hosts.'
        );
        return;
      }
      onSuccess && onSuccess(data.user);
    } catch (err) {
      const base = getApiBase();
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const attemptedUrl = `${base || origin}/auth/tool/login`;
      const msg = err?.message || '';
      const looksLikeNetwork =
        msg === 'Failed to fetch' ||
        /network|fetch|ECONNREFUSED|Load failed|NetworkError/i.test(msg);
      if (looksLikeNetwork) {
        setError(
          `Cannot reach the TideSync API (${attemptedUrl}). Start the backend: npm start (port 3002). Confirm http://localhost:3002/api/health shows ok. Set REACT_APP_API_URL if the React app uses another host.`
        );
      } else {
        setError(msg ? `${msg} (requested: ${attemptedUrl})` : `Request failed: ${attemptedUrl}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-cyan-50 via-teal-50/50 to-blue-50 p-4">
      <div className="card-panel w-full max-w-md border-cyan-200/60 p-8 shadow-xl">
        <div className="mb-6">
          <img
            src="/tidesync-logo.png?v=2"
            alt="TideSync — Time punches to OTL"
            className="mx-auto h-auto w-full max-h-20 object-contain object-center"
          />
          <p className="mt-4 text-center text-sm text-slate-500">
            Load punches into Oracle Time and Labor
          </p>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mt-3 w-full text-center text-sm font-medium text-teal-700 hover:underline"
            >
              Back to TideSync
            </button>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="tool-login" className="mb-1 block text-sm font-medium text-slate-700">
              Email or TideSync username
            </label>
            <input
              id="tool-login"
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="your@email.com or username"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 focus:border-slate-400 focus:ring-2 focus:ring-slate-400"
              autoComplete="username"
            />
          </div>
          <div>
            <label htmlFor="tool-password" className="mb-1 block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="tool-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 focus:border-slate-400 focus:ring-2 focus:ring-slate-400"
              autoComplete="current-password"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-md hover:from-cyan-700 hover:to-teal-700 disabled:opacity-50"
          >
            <LogIn className="h-4 w-4" />
            {loading ? 'Please wait…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          New user?{' '}
          <a href={WEBSITE_SIGNUP_URL} className="font-medium text-teal-700 hover:underline">
            Create your SLP account
          </a>
        </p>
        <p className="mt-3 text-center text-xs text-slate-400">
          Using server:{' '}
          {typeof window !== 'undefined' ? getApiBase() || window.location.origin + ' (proxy)' : '…'}
        </p>
      </div>
    </div>
  );
}
